import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { getServizioIcon } from "@/lib/icon-registry";
import { useHomeWallSettings, buildWallGridClasses, wallDefaults } from "@/hooks/useHomeWallSettings";

interface ProjectItem {
  name: string;
  slug: string;
  iconType?: "private-label" | "hotellerie" | "ristorazione" | "regalistica" | "esperienze";
  description?: string;
  heroImage?: string | null;
}

interface ProjectsGridProps {
  id?: string;
  eyebrow?: string;
  title: string;
  description: string;
  projects: ProjectItem[];
}

const FALLBACK_HERO = "/lovable-uploads/PROGETTI.png";

const ProjectsGridItem = ({ p }: { p: ProjectItem }) => {
  const Icon = getServizioIcon(p.iconType || p.slug);
  const hero = p.heroImage || FALLBACK_HERO;

  return (
    <Link
      to={`/progetti/${p.slug}`}
      aria-label={p.name}
      className="block"
    >
      <div className="group relative aspect-square w-full overflow-hidden border border-border bg-card transition-all duration-500 hover:border-terracotta hover:scale-[1.01] hover:z-10">
        {/* Hero background al hover */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ backgroundImage: `url(${hero})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/55 opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />

        {/* Icona + titolo: layout centrato come BrandWall (logo+nome) */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-3 transition-all duration-500 group-hover:scale-105">
          <Icon
            className="h-16 w-16 md:h-20 md:w-20 text-foreground/70 transition-[filter,color] duration-500 group-hover:text-white group-hover:[filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.6))]"
            strokeWidth={1.5}
          />
          <h3 className="mt-3 sm:mt-4 font-display text-base sm:text-lg md:text-xl leading-tight text-foreground/85 text-center transition-colors duration-500 group-hover:text-white">
            {p.name}
          </h3>
        </div>

        {/* Reveal: solo freccia in basso a destra */}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-end p-3.5 sm:p-4 opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
          <ArrowUpRight className="h-5 w-5 text-white shrink-0 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" }} />
        </div>
      </div>
    </Link>
  );
};

const ProjectsGrid = ({ id, eyebrow, title, description, projects }: ProjectsGridProps) => {
  const { data: settings } = useHomeWallSettings("progetti");
  const s = settings || wallDefaults["progetti"];
  if (!s.is_visible) return null;

  let visible = projects;
  if (s.max_items && s.max_items > 0) visible = visible.slice(0, s.max_items);

  return (
    <section
      id={id}
      className="py-5 md:py-7 border-t border-border bg-background scroll-mt-20"
    >
      <div className="container mx-auto px-6">
        <SectionHeader eyebrow={eyebrow} title={title} description={description} />
        <div className={buildWallGridClasses(s)}>
          {visible.map((p) => (
            <ProjectsGridItem key={p.slug} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsGrid;
